// src/receptionist/merchantState.tsx
//
// One place that loads a merchant's config and saves it back
// (ourteam-frontend#3607, BE ourteam-backend#4667). Four of the seven console
// screens are views onto this one object, so the fetch, the four render
// states and the save rules live here once rather than four times.
//
// The save rule that matters: after a PUT, the screen shows the object the
// server RETURNED, never its own optimistic copy. The server recomputes the
// escalation locks from `category`, so an optimistic copy is wrong the moment
// a category changes — and a 422 (a locked rule switched off, a half-set
// hours row) leaves the server's copy untouched, which is what the form must
// then show.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AlertTriangleIcon, Loader2Icon, SearchXIcon } from "lucide-react";
import { Card, CardBody, EmptyState, Button } from "./components/kit";
import { useSnackbar } from "@hook/use-snackbar";
import { getMerchant, saveMerchant, type MerchantConfig } from "@service/receptionist";
import { nameAnnounced, nameReceived, nameToSave, newNameTracker } from "./nameTracker";

/** What every merchant-config screen takes instead of reading a fixture. */
export type MerchantScreenProps = {
    merchant: MerchantConfig;
    /** Resolves true when the server accepted the save. The screen clears its
     *  dirty flag on true and keeps it on false; either way the `merchant`
     *  prop it receives next is the server's copy. */
    onSave: (next: MerchantConfig) => Promise<boolean>;
};

/**
 * Fired whenever a merchant's config is read or saved, so the sidebar's
 * business name follows a rename on Business profile without a reload.
 */
export const MERCHANT_NAME_EVENT = "rcp:merchant-name";
export type MerchantNameDetail = { id: string; name: string };
function announceName(id: string, name: string) {
    window.dispatchEvent(new CustomEvent<MerchantNameDetail>(MERCHANT_NAME_EVENT, { detail: { id, name } }));
}
/** For a save made outside a screen: the rename from the sidebar's pencil. */
export const announceMerchantName = announceName;

type State =
    | { kind: "loading" }
    | { kind: "notFound" }
    | { kind: "error" }
    | { kind: "ready"; merchant: MerchantConfig };

export function useMerchant(merchantId: string | null) {
    const [state, setState] = useState<State>({ kind: "loading" });
    // Bumped on every fetch so a response for an earlier merchant cannot land
    // after a later one when staff switch quickly.
    const seq = useRef(0);
    const snackbar = useSnackbar();
    // The name this screen was handed and the newest one announced (#4937):
    // see nameTracker. The sidebar's pencil renames without touching this
    // state, so a save from a screen still carrying the old name sends the new
    // one, and a read or save that was out during a rename never announces the
    // old name back (Jack, #4938).
    const names = useRef(newNameTracker());

    const load = useCallback(async () => {
        const mine = ++seq.current;
        if (!merchantId) { setState({ kind: "notFound" }); return; }
        setState({ kind: "loading" });
        const revisionAtStart = names.current.revision;
        const result = await getMerchant(merchantId);
        if (seq.current !== mine) return;
        if (result.ok) {
            const newest = nameReceived(names.current, result.merchant.name, revisionAtStart);
            setState({ kind: "ready", merchant: result.merchant });
            if (newest) announceName(merchantId, result.merchant.name);
        } else setState({ kind: result.kind === "notFound" ? "notFound" : "error" });
    }, [merchantId]);

    // Before the load below (effects run in order): a new merchant starts with
    // a fresh tracker, never comparing against the previous merchant's names.
    useEffect(() => {
        names.current = newNameTracker();
        const onName = (e: Event) => {
            const d = (e as CustomEvent<MerchantNameDetail>).detail;
            if (d?.id === merchantId) nameAnnounced(names.current, d.name);
        };
        window.addEventListener(MERCHANT_NAME_EVENT, onName);
        return () => window.removeEventListener(MERCHANT_NAME_EVENT, onName);
    }, [merchantId]);

    useEffect(() => { void load(); }, [load]);

    const save = useCallback(async (draft: MerchantConfig): Promise<boolean> => {
        if (!merchantId) return false;
        const revisionAtStart = names.current.revision;
        const next = { ...draft, name: nameToSave(names.current, draft.name) };
        const result = await saveMerchant(merchantId, next);
        if (result.ok) {
            const newest = nameReceived(names.current, result.merchant.name, revisionAtStart);
            // The server's object, not the draft: locks and read-only fields
            // are the server's to compute.
            setState({ kind: "ready", merchant: result.merchant });
            if (newest) announceName(merchantId, result.merchant.name);
            snackbar.showSuccess("Saved.");
            return true;
        }
        // A rejected save leaves the server's copy as it was. Say why, then
        // put that copy back on screen so the form matches the server.
        snackbar.showError(result.message);
        const revisionAtRead = names.current.revision;
        const fresh = await getMerchant(merchantId);
        if (fresh.ok) {
            nameReceived(names.current, fresh.merchant.name, revisionAtRead);
            setState({ kind: "ready", merchant: fresh.merchant });
        }
        return false;
    }, [merchantId, snackbar]);

    return { state, save, reload: load };
}

/**
 * Renders the four states around a merchant-config screen. `children` gets the
 * merchant and the save callback only once it is loaded.
 */
export function MerchantGate({
    merchantId,
    children,
}: {
    merchantId: string | null;
    children: (props: MerchantScreenProps) => ReactNode;
}) {
    const { state, save, reload } = useMerchant(merchantId);

    if (state.kind === "loading") {
        return (
            <div className="flex justify-center py-24">
                <Loader2Icon className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }
    if (state.kind === "notFound") {
        // No retry: the backend answers 404 for "does not exist" AND "not
        // yours", on purpose, and neither is fixed by asking again.
        return (
            <div>
                <Card>
                    <CardBody>
                        <EmptyState
                            icon={SearchXIcon}
                            title="This merchant isn't available to you"
                            description="Either it does not exist or your account is not linked to it."
                        />
                    </CardBody>
                </Card>
            </div>
        );
    }
    if (state.kind === "error") {
        return (
            <div>
                <Card>
                    <CardBody>
                        <EmptyState
                            icon={AlertTriangleIcon}
                            title="Couldn't load this merchant"
                            description="Something went wrong asking for the configuration."
                            action={<Button onClick={() => void reload()}>Try again</Button>}
                        />
                    </CardBody>
                </Card>
            </div>
        );
    }
    return <>{children({ merchant: state.merchant, onSave: save })}</>;
}
